import {
    FormControl,
    FormLabel,
    Select
} from '@chakra-ui/react';
import { CheckboxFormControl, SelectFormControl } from 'BrokerTsLib/src/components';
import type { PersonAdditionalData } from 'BrokerTsLib/src/dtos';
import { Insurer, Language, Product, Selection, SelectionType } from 'BrokerTsLib/src/enums';
import { useFormContext } from 'BrokerTsLib/src/rhf';

import RuianAddressSelector from '@/components/RuianAddressSelector';
import { FormBody } from '@/containers/forms/FormBody';
import { FormRow } from '@/containers/forms/FormRow';
import AgentPerson from '@/containers/forms/person/AgentPerson';
import ICOOptions from '@/containers/forms/person/ICOOptions';
import InsureeContact from '@/containers/forms/person/InsureeContact';
import InsureeName from '@/containers/forms/person/InsureeName';
import { PersonID } from '@/containers/forms/person/PersonID';
import PersonType from '@/containers/forms/person/PersonType';
import { useInsuranceParams } from '@/context/InsuranceParamsProvider';
import { useSelectedQuote } from '@/context/SelectedQuoteContext';
import { useSelections } from '@/hooks';
import { useApp } from '@/hooks/useApp';
import { useNameof } from '@/hooks/useNameof';
import { Agreements } from './Agreements';

export type AdditionalPolicyHolderInfoFormProps = {
  personPrefix: string
  simpleShow?: boolean
}

export const AdditionalPolicyHolderInfoForm = ({
  personPrefix,
  simpleShow = false
}: AdditionalPolicyHolderInfoFormProps) => {
  const { product } = useApp()
  const { isDisabled } = useInsuranceParams()

  const { offer } = useSelectedQuote()

  const { watch, register, getValues, control } = useFormContext()

  const pnameof = useNameof<PersonAdditionalData>(personPrefix)

  const { data: dataTitles } = useSelections(SelectionType.TitleBefore)

  const pType = watch(pnameof((x) => x.type))
  const showAgent = watch(pnameof((x) => x.agent) + '.required')

  return (
    <>
      <FormBody>
        {!simpleShow && <PersonType personPrefix={personPrefix} disabled={true} />}

        {[Selection.Company, Selection.SelfEmployed].includes(pType) && !simpleShow && (
          <CheckboxFormControl name={pnameof(x => x.isVatPayer)} control={control} label='Plátce DPH' isDisabled={true} />
        )}

        {[Selection.Person, Selection.SelfEmployed].includes(pType) && !simpleShow && (
          <PersonID personPrefix={personPrefix} disabled={true} />
        )}

        {[Selection.Company, Selection.SelfEmployed].includes(pType) && !simpleShow && (
          <ICOOptions personPrefix={personPrefix} disabled={true} />
        )}

        {[Selection.Person, Selection.SelfEmployed].includes(pType) && (
          <FormRow itemsCount={3}>
            <FormControl>
              <FormLabel>Titul před jménem</FormLabel>

              {(dataTitles?.data.length ?? 0) > 0 && (
                <Select
                  {...register(pnameof((x) => x.titleBefore))}
                  isDisabled={isDisabled}
                  defaultValue={getValues(pnameof((x) => x.titleBefore)) ?? ''}
                >
                  <option value="">Vyberte</option>
                  {dataTitles?.data.map((item) => (
                    <option value={item.value?.toString()} key={item.id}>
                      {item.value}
                    </option>
                  ))}
                </Select>
              )}
            </FormControl>

            <InsureeName personPrefix={personPrefix} disabled={true} />

            {/* <FormControl>
              <FormLabel>Titul za jménem</FormLabel>

              <InputGroup
                {...register(pnameof(x => x.titleAfter))}
                isDisabled={isDisabled}
              />
            </FormControl> */}
          </FormRow>
        )}

        {(product == Product.TravelInsurance || !simpleShow) && <RuianAddressSelector
          addressPath={pnameof((x) => x.address)}
          isDisabled={product !== Product.TravelInsurance || isDisabled}
          showOnlyLotNumber={false}
        />}

        {showAgent && <AgentPerson personPrefix={personPrefix} />}

        <InsureeContact personPrefix={personPrefix} isDisabled={isDisabled} />

        {offer?.insurerId == Insurer.Hellas && (
          <FormRow itemsCount={3}>
            <SelectFormControl control={control} name={pnameof((x) => x.policyHolderLanguage)} isDisabled={isDisabled}
              label="Jazyk komunikace"
              options={[
                { label: 'Čeština', value: Language.CZ },
                { label: 'Angličtina', value: Language.EN }
              ]}
              valueAsNumber
            />
          </FormRow>
        )}

        {product != Product.VehicleLeasingInsurance &&
          <Agreements
            control={control}
            agreementName={pnameof(x => x.agreement)}
            agreementOtherName={pnameof(x => x.agreementOther)}
            agreementWarningMessageName='policyHolder.agreementWarningMessage'
            isDisabled={isDisabled}
            offer={offer}
          />
        }
      </FormBody>
    </>
  )
}
